// src/modules/professor/ProfessorAvailability.js
import React, { useState, useEffect } from 'react';
import { db, paths } from '../../firebase/config';
import { doc, getDoc, setDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { useAuth, logAuditoria } from '../../context/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/Button';

// Ícones
const SaveIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>;
const BanIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>;
const CalendarIcon = () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2H5a2 2 0 00-2-2H5a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
const ChevronLeftIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>;
const ChevronRightIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
const ClassIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>;
const AlertIcon = () => <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.398 16c-.77 1.333.192 3 1.732 3z" /></svg>;

const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const TEMPOS_AULA = ['1º Tempo', '2º Tempo', '3º Tempo', '4º Tempo', '5º Tempo', '6º Tempo'];

// --- Funções Auxiliares de Data ---
const getWeekString = (date = new Date()) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const year = d.getUTCFullYear();
    const weekNo = Math.ceil((((d - new Date(Date.UTC(year, 0, 1))) / 86400000) + 1) / 7);
    return `${year}-W${String(weekNo).padStart(2, '0')}`;
};

const getDateRangeFromWeek = (weekString) => {
    if (!weekString) return "";
    const [yearStr, weekStr] = weekString.split('-W');
    const year = parseInt(yearStr, 10);
    const week = parseInt(weekStr, 10);
    
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    
    const start = new Date(ISOweekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    
    const fmt = d => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    return `${fmt(start)} a ${fmt(end)}`;
};

export const ProfessorAvailability = () => {
    const { userProfile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    
    const [selectedWeek, setSelectedWeek] = useState(getWeekString());
    const [impedimentosMap, setImpedimentosMap] = useState({});
    const [minhasAulas, setMinhasAulas] = useState({});
    const [pelotoesMap, setPelotoesMap] = useState({});

    useEffect(() => {
        const unsub = onSnapshot(collection(db, paths.pelotoes), (snap) => {
            const map = {};
            snap.docs.forEach(d => { map[d.id] = d.data().nome; });
            setPelotoesMap(map);
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!userProfile?.id || !selectedWeek) return;
        setLoading(true);

        const fetchImpedimentos = async () => {
            try {
                const docRef = doc(db, paths.impedimentos, userProfile.id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setImpedimentosMap(data[selectedWeek]?.slots || {});
                } else {
                    setImpedimentosMap({});
                }
            } catch (error) {
                console.error("Erro ao carregar impedimentos:", error);
            }
        };

        const qCronogramas = query(collection(db, paths.cronogramas), where("semana", "==", selectedWeek));
        const unsubCronogramas = onSnapshot(qCronogramas, (snap) => {
            const aulasMap = {};
            snap.docs.forEach(docSnap => {
                const data = docSnap.data();
                const pelotaoId = data.pelotao_id;
                const grade = data.grade || {};
                Object.entries(grade).forEach(([key, aula]) => {
                    if (aula.professor_id === userProfile.id) {
                        aulasMap[key] = {
                            materia: aula.materia_nome,
                            pelotaoId: pelotaoId,
                            tipo: aula.tipo
                        };
                    }
                });
            });
            setMinhasAulas(aulasMap);
            setLoading(false); 
        });

        fetchImpedimentos();
        return () => unsubCronogramas();
    }, [userProfile, selectedWeek]);

    const handleWeekChange = (offset) => {
        const [yearStr, weekStr] = selectedWeek.split('-W');
        let year = parseInt(yearStr, 10);
        let week = parseInt(weekStr, 10);
        week += offset;
        if (week > 52) { week = 1; year++; }
        else if (week < 1) { week = 52; year--; }
        setSelectedWeek(`${year}-W${String(week).padStart(2, '0')}`);
    };

    const toggleSlot = (dia, tempo) => {
        const key = `${dia}-${tempo}`;
        setImpedimentosMap(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = async () => {
        setProcessing(true);
        try {
            const docRef = doc(db, paths.impedimentos, userProfile.id);
            await setDoc(docRef, {
                professor_id: userProfile.id,
                professor_nome: userProfile.nome,
                [selectedWeek]: {
                    slots: impedimentosMap,
                    atualizado_em: new Date().toISOString()
                }
            }, { merge: true });
            await logAuditoria(userProfile, "Atualizou Impedimentos de Horário", { semana: selectedWeek });
            alert("Disponibilidade salva com sucesso para a semana " + selectedWeek);
        } catch (error) { alert("Erro ao salvar: " + error.message); } 
        finally { setProcessing(false); }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-4 animate-fade-in flex flex-col h-[calc(100vh-100px)]">
            {/* CABEÇALHO COMPACTO */}
            <header className="flex flex-col md:flex-row justify-between items-center gap-2 mb-2 flex-shrink-0">
                <div className="flex flex-col">
                    <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <CalendarIcon className="w-5 h-5" /> Minha Agenda
                    </h1>
                    <p className="text-xs text-gray-500 ml-7">
                        Período: <strong>{getDateRangeFromWeek(selectedWeek)}</strong>
                    </p>
                    <div className="ml-7 mt-1 flex flex-wrap gap-2 text-[10px] text-gray-500">
                        <div className="flex items-center gap-1"><span className="w-2 h-2 bg-white border border-gray-300"></span> Disponível</div>
                        <div className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500"></span> Indisponível</div>
                        <div className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-50 border border-blue-200"></span> Aula Atribuída</div>
                        <div className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-100 border border-orange-300"></span> Conflito</div>
                    </div>
                </div>
                
                <div className="flex items-center gap-1 bg-white p-1 rounded-lg shadow-sm border border-gray-200">
                    <button onClick={() => handleWeekChange(-1)} className="p-1 hover:bg-gray-100 rounded text-gray-600"><ChevronLeftIcon className="w-4 h-4" /></button>
                    <input type="week" value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)} className="border-none bg-transparent font-bold text-xs text-center focus:ring-0 w-32"/>
                    <button onClick={() => handleWeekChange(1)} className="p-1 hover:bg-gray-100 rounded text-gray-600"><ChevronRightIcon className="w-4 h-4" /></button>
                </div>
            </header>

            {/* TABELA COM SCROLL SE NECESSÁRIO, MAS COMPACTA */}
            <div className="bg-white p-2 rounded-lg shadow border border-gray-200 flex-grow overflow-auto">
                <table className="w-full table-fixed border-collapse">
                    <thead className="sticky top-0 bg-white z-10 shadow-sm">
                        <tr>
                            <th className="p-2 border bg-gray-100 text-left text-[10px] font-bold text-gray-600 uppercase w-20">Horário</th>
                            {DIAS_SEMANA.map(dia => (
                                <th key={dia} className="p-2 border bg-gray-100 text-center text-[10px] font-bold text-gray-600 uppercase">
                                    {dia.substring(0, 3)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {TEMPOS_AULA.map(tempo => (
                            <tr key={tempo}>
                                <td className="p-2 border font-bold text-[10px] text-gray-700 bg-gray-50 align-middle">
                                    {tempo}
                                </td>
                                {DIAS_SEMANA.map(dia => {
                                    const key = `${dia}-${tempo}`;
                                    const isBlocked = impedimentosMap[key];
                                    const aula = minhasAulas[key];
                                    const pelotaoNome = aula ? (pelotoesMap[aula.pelotaoId] || '...') : '';

                                    let cellClass = "bg-white hover:bg-gray-50";
                                    let content = null; // Vazio se disponível

                                    if (aula) {
                                        if (isBlocked) {
                                            cellClass = "bg-orange-50 border-orange-200";
                                            content = (
                                                <div className="flex flex-col items-center justify-center h-full text-orange-800 relative">
                                                    <span className="font-bold text-[9px] uppercase leading-tight text-center truncate w-full px-1">{aula.materia}</span>
                                                    <span className="text-[8px] bg-orange-200 px-1 rounded mt-0.5">{pelotaoNome}</span>
                                                    <div className="absolute top-0 right-0 p-0.5 text-red-500" title="Impedimento Sinalizado"><AlertIcon /></div>
                                                </div>
                                            );
                                        } else {
                                            cellClass = "bg-blue-50 border-blue-100 hover:bg-blue-100";
                                            content = (
                                                <div className="flex flex-col items-center justify-center h-full text-blue-900">
                                                    <span className="font-bold text-[9px] uppercase text-center leading-tight truncate w-full px-1">{aula.materia}</span>
                                                    <span className="text-[8px] mt-0.5 bg-blue-200 text-blue-800 px-1 py-0 rounded-full">{pelotaoNome}</span>
                                                </div>
                                            );
                                        }
                                    } else if (isBlocked) {
                                        cellClass = "bg-red-500 text-white hover:bg-red-600";
                                        content = (
                                            <div className="flex flex-col items-center justify-center">
                                                <BanIcon className="w-3 h-3 mb-0.5" />
                                                <span className="text-[8px] font-bold">NÃO</span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <td 
                                            key={key} 
                                            onClick={() => toggleSlot(dia, tempo)}
                                            className={`p-1 border text-center cursor-pointer transition-all duration-200 h-14 align-middle ${cellClass}`}
                                        >
                                            {content}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* BOTÃO FIXO NO RODAPÉ DO CONTAINER */}
            <div className="flex justify-end pt-2 flex-shrink-0">
                <Button onClick={handleSave} variant="primary" disabled={processing} className="flex items-center gap-2 text-sm py-2 px-4 shadow-lg">
                    <SaveIcon className="w-4 h-4" /> {processing ? "Salvando..." : "Salvar Alterações"}
                </Button>
            </div>
        </div>
    );
};